import { c as _c } from "react/compiler-runtime";
import { useProgressApiClient } from "@api/progress-api/use-progress-api-client";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { snapshotsBeingCreatedCountSelector } from "@store/projects/projects-selector";
import { fetchProjectSnapshots, fetchSnapshotsBeingCreatedCount } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { useEffect, useRef } from "react";

/** Interval in milliseconds to poll the progress API for snapshots being created */
const POLLING_INTERVAL = 10_000;

/**
 * Polls the progress API to track how many snapshots are currently being created for a project
 * and updates the store with the count.
 *
 * - Only users with permission to view the project cloud activity fetch the data.
 * - Polls at a regular interval while the snapshots tab is mounted.
 * - Reloads the snapshots table once whenever the number of snapshots being created decreases,
 *   so newly created snapshots show up in the table.
 */
export function useSnapshotsBeingCreatedTracker(t0) {
  const $ = _c(12);
  const {
    projectId
  } = t0;
  const dispatch = useAppDispatch();
  let t1;
  if ($[0] !== projectId) {
    t1 = {
      projectId
    };
    $[0] = projectId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const progressApiClient = useProgressApiClient(t1);
  const {
    canViewProjectCloudActivity
  } = useHasUserValidPermissionProjectLevel();
  const snapshotsBeingCreatedCount = useAppSelector(snapshotsBeingCreatedCountSelector);
  const previousCountRef = useRef(snapshotsBeingCreatedCount);
  let t2;
  let t3;
  if ($[2] !== dispatch || $[3] !== projectId || $[4] !== snapshotsBeingCreatedCount) {
    t2 = () => {
      if (snapshotsBeingCreatedCount < previousCountRef.current) {
        dispatch(fetchProjectSnapshots({
          projectId
        }));
      }
      previousCountRef.current = snapshotsBeingCreatedCount;
    };
    t3 = [snapshotsBeingCreatedCount, dispatch, projectId];
    $[2] = dispatch;
    $[3] = projectId;
    $[4] = snapshotsBeingCreatedCount;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[7] !== canViewProjectCloudActivity || $[8] !== dispatch || $[9] !== progressApiClient) {
    t4 = () => {
      if (!canViewProjectCloudActivity) {
        return;
      }
      const fetchCount = function fetchCount() {
        dispatch(fetchSnapshotsBeingCreatedCount({
          progressApiClient
        }));
      };
      fetchCount();
      const intervalId = setInterval(fetchCount, POLLING_INTERVAL);
      return () => clearInterval(intervalId);
    };
    t5 = [canViewProjectCloudActivity, dispatch, progressApiClient];
    $[7] = canViewProjectCloudActivity;
    $[8] = dispatch;
    $[9] = progressApiClient;
    $[10] = t4;
    $[11] = t5;
  } else {
    t4 = $[10];
    t5 = $[11];
  }
  useEffect(t4, t5);
}